import { type useContentProgressResponse } from 'ContentProgress';
import { type StreamForStreamDashboard, type LessonForStreamDashboard } from 'Lessons';
import { type StoredStreamForDashboard } from '../StreamDashboard.types';

/*
    Builds the dashboard stream from the offline store record. The record has no progress, so progress comes
    from ContentProgress. Recommended and related cards stay empty offline, because they need a fetch.
*/
export function adaptStoredStreamForDashboard(
    stream: StoredStreamForDashboard,
    contentProgress: Pick<useContentProgressResponse, 'streamProgressByLocalePackId' | 'lessonProgressByLocalePackId'>,
): StreamForStreamDashboard {
    const { streamProgressByLocalePackId, lessonProgressByLocalePackId } = contentProgress;

    const lessons: LessonForStreamDashboard[] = stream.lessons.map(lesson => {
        const localePackId = lesson.localePack.id;

        return {
            id: lesson.id,
            versionId: lesson.versionId,
            title: lesson.title,
            tag: lesson.tag,
            locale: lesson.locale ?? stream.locale,
            localePack: lesson.localePack,
            localePackId,
            itemVersionKey: lesson.itemVersionKey,
            frameCount: lesson.frameCount,
            assessment: lesson.assessment,
            test: lesson.test,
            unrestricted: lesson.unrestricted,
            publishedAt: lesson.publishedAt ?? stream.publishedAt,
            description: lesson.description ?? [],
            keyTerms: lesson.keyTerms ?? null,
            keyTermDefinitions: lesson.keyTermDefinitions ?? null,
            entityMetadata: lesson.entityMetadata ?? null,
            lessonProgress: lessonProgressByLocalePackId[localePackId] ?? lesson.lessonProgress ?? null,
        };
    });

    return {
        ...stream,
        lessons,
        versionId: stream.versionId ?? '',
        description: stream.description ?? '',
        beta: stream.beta ?? false,
        comingSoon: stream.comingSoon ?? false,
        favorite: stream.favorite ?? false,
        credits: stream.credits ?? null,
        whatYouWillLearn: stream.whatYouWillLearn ?? null,
        recommendedStreamIds: stream.recommendedStreamIds ?? [],
        relatedStreamIds: stream.relatedStreamIds ?? [],
        resourceDownloads: stream.resourceDownloads ?? [],
        resourceLinks: stream.resourceLinks ?? [],
        summaries: stream.summaries ?? [],
        timeLimitHours: stream.timeLimitHours ?? null,
        entityMetadata: stream.entityMetadata ?? null,
        lessonStreamsProgress:
            streamProgressByLocalePackId[stream.localePackId] ?? stream.lessonStreamsProgress ?? null,
    };
}
